import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Submissions from './pages/Submissions';
import Projects from './pages/Projects';
import UserManagement from './pages/UserManagement';
import RoleManagement from './pages/RoleManagement';
import Settings from './pages/Settings';
import Visitors from './pages/Visitors';
import VisitorDashboard from './pages/VisitorDashboard';
import Login from './pages/Login';
import { can, PAGE_PERMISSION } from './utils/permissions';
import { AUTH_KEY } from './utils/auth';
import { getEncKey, setEncKey } from './utils/crypto';
import './App.css';

const DEFAULT_ENC_KEY = 'GpEOI$SecureKey2024@AdminPortal!';
// Always sync the key — overrides any stale value from localStorage/Settings
setEncKey(DEFAULT_ENC_KEY);

const VALID_PAGES = ['dashboard', 'submissions', 'projects', 'users', 'roles', 'settings', 'visitors', 'visitor_dashboard'];
const DEFAULT_PAGE = 'dashboard';

function pageFromHash() {
  const raw = (window.location.hash || '').replace(/^#\/?/, '').split('?')[0].trim();
  return VALID_PAGES.includes(raw) ? raw : DEFAULT_PAGE;
}

export default function App() {
  const [page, setPage] = useState(pageFromHash);
  const [auth, setAuth] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);

  // Load auth from localStorage on first mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(AUTH_KEY);
      if (raw) setAuth(JSON.parse(raw));
    } catch { /* ignore */ }
    setBootstrapped(true);
  }, []);

  // Listen for hash changes (back/forward, manual edit)
  useEffect(() => {
    const onHash = () => setPage(pageFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const user = auth?.user;
  const isAllowed = (key) => VALID_PAGES.includes(key) && can(user, PAGE_PERMISSION[key]);
  const firstAllowedPage = VALID_PAGES.find(isAllowed) || DEFAULT_PAGE;

  // Keep URL hash in sync, and bounce the user off any page their role can't
  // access (deep-link, stale hash, or a page they just lost permission to).
  useEffect(() => {
    if (!auth) return;
    const safePage = isAllowed(page) ? page : firstAllowedPage;
    if (safePage !== page) setPage(safePage);
    const target = `#/${safePage}`;
    if (window.location.hash !== target) {
      window.history.replaceState(null, '', target);
    }
  }, [page, auth]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavigate = (key) => {
    if (!isAllowed(key)) return;
    window.location.hash = `#/${key}`;
    setPage(key);
  };

  const handleLogin = (data) => {
    localStorage.setItem(AUTH_KEY, JSON.stringify(data));
    setAuth(data);
  };

  const handleLogout = () => {
    localStorage.removeItem(AUTH_KEY);
    setAuth(null);
  };

  if (!bootstrapped) return null;
  if (!auth) return <Login onLogin={handleLogin} />;

  return (
    <div className="app">
      <Sidebar active={page} onChange={handleNavigate} user={auth.user} onLogout={handleLogout} />
      <main className="app-main">
        {/* Unified, centered content container shared by every page */}
        <div className="app-container">
          {page === 'dashboard'         && isAllowed('dashboard')         && <Dashboard onNavigate={handleNavigate} />}
          {page === 'submissions'       && isAllowed('submissions')       && <Submissions />}
          {page === 'projects'          && isAllowed('projects')          && <Projects />}
          {page === 'users'             && isAllowed('users')             && <UserManagement />}
          {page === 'roles'             && isAllowed('roles')             && <RoleManagement />}
          {page === 'settings'          && isAllowed('settings')          && <Settings />}
          {page === 'visitors'          && isAllowed('visitors')          && <Visitors />}
          {page === 'visitor_dashboard' && isAllowed('visitor_dashboard') && <VisitorDashboard />}
        </div>
      </main>
    </div>
  );
}
